import React, { useEffect, useMemo, useState } from "react";
import {
  Typography,
  List,
  Card,
  Row,
  Col,
  Input,
  Select,
  Button,
  Tag,
  Space,
  Spin,
  message,
  Empty,
  Grid,
  Popconfirm,
  Alert,
  Drawer,
  Descriptions,
  Timeline,
  Tooltip,
} from "antd";
import {
  ReloadOutlined,
  PhoneOutlined,
  FilterOutlined,
  SearchOutlined,
  CheckCircleOutlined,
  InfoCircleOutlined,
  WarningOutlined,
  WhatsAppOutlined,
  SafetyCertificateOutlined,
} from "@ant-design/icons";
import axios from "axios";
import moment from "moment";

const { Title, Text, Paragraph } = Typography;
const { useBreakpoint } = Grid;

const getStoredToken = () => {
  const keys = ["token", "accessToken", "AuthToken", "jwtToken"];
  for (const k of keys) {
    const v = localStorage.getItem(k);
    if (v) return v.replace(/^"|"$/g, "");
  }
  try {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    return user?.token || user?.accessToken || null;
  } catch {
    return null;
  }
};

const api = axios.create({
  baseURL: global.config.APIURL.BaseURL,
  timeout: 15000,
});

api.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers = {
      ...(config.headers || {}),
      Authorization: `Bearer ${token}`,
    };
  }
  if (config.withCredentials === undefined) {
    config.withCredentials = false;
  }
  return config;
});

const fmtDate = (d) => (d ? moment(d).format("DD MMM YYYY") : "-");
const fmtDateTime = (d) => (d ? moment(d).format("DD MMM YYYY, hh:mm A") : "-");
const fmtDuration = (sec) => {
  if (sec === null || sec === undefined) return null;
  const m = Math.floor(sec / 60);
  const s = String(sec % 60).padStart(2, "0");
  return `${m}:${s} min`;
};

const statusTagColor = (status) => {
  const s = (status || "").toLowerCase();
  if (s.includes("lost")) return "red";
  if (s.includes("convert") || s.includes("win") || s.includes("approv")) return "green";
  if (s.includes("response")) return "default";
  return "blue";
};

const whatsAppStatusColor = (status) => {
  switch ((status || "").toLowerCase()) {
    case "read":
      return "green";
    case "delivered":
      return "blue";
    case "failed":
      return "red";
    default:
      return "default";
  }
};

// Business type the account gets on approval, from the WhatsApp (BotBiz) labels.
const businessTypes = (labels) =>
  labels
    ? [
        labels.OnlineSeller && "Online Seller",
        labels.Wholesaler && "Wholesaler",
        labels.Retailer && "Retailer",
      ].filter(Boolean)
    : [];

const LeadsToApprove = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [approvingId, setApprovingId] = useState(null);

  const [search, setSearch] = useState("");
  const [inquiryFilter, setInquiryFilter] = useState(undefined);
  const [stateFilter, setStateFilter] = useState(undefined);
  const [visibleCount, setVisibleCount] = useState(10);

  const [detailLead, setDetailLead] = useState(null);
  // WhatsApp labels per LeadID: { loading, data, error }
  const [labelsByLead, setLabelsByLead] = useState({});

  useEffect(() => {
    fetchLeads(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchLeads = async (initial = false) => {
    try {
      if (initial) setLoading(true);
      else setFetching(true);

      const res = await api.get("api/HighIntentLeads/GetHighIntentLeads");
      const data = Array.isArray(res.data) ? res.data : [];
      setLeads(data);
      setVisibleCount(isMobile ? 10 : 20);
    } catch (err) {
      if (err?.response) {
        if (err.response.status === 401) {
          messageApi.error("Unauthorized. Please log in again.");
        } else {
          messageApi.error(`Failed to fetch leads (${err.response.status}).`);
        }
        console.error("API error:", err.response);
      } else if (err?.request) {
        messageApi.error(
          "Network/CORS error. Check SSL, CORS, or ad-blockers."
        );
        console.error("Request error (no response):", err.request);
      } else {
        messageApi.error("Unexpected error occurred.");
        console.error("Unexpected error:", err.message);
      }
    } finally {
      setLoading(false);
      setFetching(false);
    }
  };

  // One BotBiz call per lead, so only fetched when a lead is opened or about to be approved.
  const loadLabels = async (leadId, force = false) => {
    if (!leadId) return;
    const cached = labelsByLead[leadId];
    if (!force && cached && (cached.loading || cached.data)) return;
    setLabelsByLead((prev) => ({ ...prev, [leadId]: { loading: true } }));
    try {
      const res = await api.get("api/HighIntentLeads/GetLeadWhatsAppLabels", {
        params: { LeadID: leadId },
      });
      setLabelsByLead((prev) => ({ ...prev, [leadId]: { data: res.data } }));
    } catch {
      setLabelsByLead((prev) => ({ ...prev, [leadId]: { error: true } }));
    }
  };

  const openDetails = (lead) => {
    setDetailLead(lead);
    loadLabels(lead?.LeeadID);
  };

  const handleConfirmApprove = async (lead) => {
    const id = lead?.LeeadID;
    if (!id) {
      messageApi.error("Lead ID missing.");
      return;
    }
    setApprovingId(id);
    try {
      const res = await api.post(
        "api/HighIntentLeads/ApproveLead",
        {},
        { params: { LeadID: id } }
      );
      const msg =
        (res?.data && (res.data.Message || res.data.message)) ||
        (typeof res?.data === "string"
          ? res.data
          : "Lead approved successfully.");
      messageApi.success(msg);
      setLeads((prev) => prev.filter((l) => l.LeeadID !== id));
      if (detailLead?.LeeadID === id) setDetailLead(null);
    } catch (err) {
      const errMsg =
        err?.response?.data?.Message ||
        err?.response?.data?.message ||
        err?.message ||
        "Failed to approve lead.";
      messageApi.error(errMsg);
    } finally {
      setApprovingId(null);
    }
  };

  const inquiryOptions = useMemo(() => {
    const set = new Set(leads.map((l) => l.InquiryType).filter(Boolean));
    return Array.from(set).map((v) => ({ label: v, value: v }));
  }, [leads]);

  const stateOptions = useMemo(() => {
    const set = new Set(leads.map((l) => l.State).filter(Boolean));
    return Array.from(set).map((v) => ({ label: v, value: v }));
  }, [leads]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return leads.filter((l) => {
      const matchesQuery =
        !q ||
        [
          l.Name,
          l.ContactNo,
          l.City,
          l.State,
          l.Source,
          l.InquiryType,
          l.FirmName,
          l.ProductName,
          l.EnquiryMessage,
          l.Email,
        ]
          .filter(Boolean)
          .some((x) => String(x).toLowerCase().includes(q));
      const matchesInquiry = !inquiryFilter || l.InquiryType === inquiryFilter;
      const matchesState = !stateFilter || l.State === stateFilter;
      return matchesQuery && matchesInquiry && matchesState;
    });
  }, [leads, search, inquiryFilter, stateFilter]);

  const visibleLeads = useMemo(
    () => filtered.slice(0, visibleCount),
    [filtered, visibleCount]
  );

  const resetFilters = () => {
    setSearch("");
    setInquiryFilter(undefined);
    setStateFilter(undefined);
    setVisibleCount(isMobile ? 10 : 20);
  };

  const loadMore = () => {
    setVisibleCount((c) => c + (isMobile ? 10 : 20));
  };

  const tagColorByInquiry = (type) => {
    switch ((type || "").toLowerCase()) {
      case "wholesale":
        return "geekblue";
      case "retail":
        return "green";
      case "custom":
      case "bespoke":
        return "purple";
      default:
        return "gold";
    }
  };

  const openDialer = (phone) => {
    if (!phone) return;
    window.location.href = `tel:${phone}`;
  };

  const accountWarning = (item) =>
    item?.HasAccount ? (
      <Alert
        type="warning"
        showIcon
        icon={<WarningOutlined />}
        style={{ padding: "6px 10px" }}
        message={
          <span style={{ fontSize: 13 }}>
            This number already has a website account
            {item.AccountFirmName ? ` (${item.AccountFirmName})` : ""}:{" "}
            <b>{item.AccountIsActive ? "Active" : "Inactive"}</b>,{" "}
            <b>{item.AccountIsApproved ? "Approved" : "Not approved"}</b>
          </span>
        }
      />
    ) : null;

  const labelsText = (leadId) => {
    const entry = labelsByLead[leadId];
    if (!entry || entry.loading) return <Text type="secondary">Checking WhatsApp labels…</Text>;
    if (entry.error) return <Text type="danger">Couldn't load WhatsApp labels</Text>;
    if (!entry.data?.Found) return <Text type="secondary">Not found on WhatsApp (BotBiz)</Text>;
    const types = businessTypes(entry.data);
    return types.length ? (
      <Space size={4} wrap>
        {types.map((t) => (
          <Tag key={t} color="purple">
            {t}
          </Tag>
        ))}
      </Space>
    ) : (
      <Text type="secondary">No business type label</Text>
    );
  };

  const approveButton = (item, size) => (
    <Popconfirm
      title="Approve this lead?"
      onOpenChange={(open) => open && loadLabels(item?.LeeadID)}
      description={
        <div style={{ lineHeight: 1.6, maxWidth: 320 }}>
          <div>
            <Text strong>Name:</Text> <Text>{item?.Name || "-"}</Text>
          </div>
          {item?.FirmName && (
            <div>
              <Text strong>Firm:</Text> <Text>{item.FirmName}</Text>
            </div>
          )}
          <div>
            <Text strong>Contact:</Text> <Text>{item?.ContactNo || "-"}</Text>
          </div>
          <div>
            <Text strong>Location:</Text>{" "}
            <Text>
              {item?.City || "-"}, {item?.State || "-"}
            </Text>
          </div>
          <div>
            <Text strong>Inquiry:</Text> <Text>{item?.InquiryType || "-"}</Text>
          </div>
          <div>
            <Text strong>Source:</Text> <Text>{item?.Source || "-"}</Text>
          </div>
          <div>
            <Text strong>Date:</Text> <Text>{fmtDate(item?.InquireDate)}</Text>
          </div>
          <div>
            <Text strong>Account type:</Text> {labelsText(item?.LeeadID)}
          </div>
          {item?.HasAccount && (
            <div style={{ marginTop: 6 }}>
              <Text type="warning">
                <WarningOutlined /> This number already has a website account.
              </Text>
            </div>
          )}
        </div>
      }
      onConfirm={() => handleConfirmApprove(item)}
      okText="Approve"
      cancelText="Cancel"
      okButtonProps={{
        type: "primary",
        icon: <CheckCircleOutlined />,
        loading: approvingId === item?.LeeadID,
      }}
      placement={isMobile ? "topRight" : "top"}
    >
      <Button
        icon={<CheckCircleOutlined />}
        size={size}
        type="primary"
        loading={approvingId === item?.LeeadID}
      >
        Approve
      </Button>
    </Popconfirm>
  );

  const field = (label, value) => (
    <>
      <Text type="secondary" style={{ fontSize: 12 }}>
        {label}
      </Text>
      <div style={{ fontSize: 13 }}>{value || "-"}</div>
    </>
  );

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#f5f6fa",
        padding: isMobile ? "8px" : "16px",
      }}
    >
      {contextHolder}

      {/* Sticky toolbar */}
      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 5,
          background: "#f5f6fa",
          paddingBottom: 8,
        }}
      >
        <Row gutter={[8, 8]} align="middle" justify="space-between">
          <Col flex="auto">
            <Title
              level={isMobile ? 4 : 3}
              style={{ margin: 0, color: "#1890ff", fontWeight: 700 }}
            >
              High Intent Leads
            </Title>
            <Text type="secondary" style={{ fontSize: isMobile ? 12 : 14 }}>
              {filtered.length} lead{filtered.length === 1 ? "" : "s"}
            </Text>
          </Col>
          <Col>
            <Button
              type="default"
              icon={<ReloadOutlined />}
              onClick={() => fetchLeads(false)}
              loading={fetching}
              size={isMobile ? "middle" : "large"}
            >
              Refresh
            </Button>
          </Col>
        </Row>

        <Card
          size="small"
          style={{
            marginTop: 8,
            borderRadius: 10,
            border: "1px solid #e6f7ff",
          }}
          bodyStyle={{ padding: isMobile ? 8 : 12 }}
        >
          <Space direction="vertical" style={{ width: "100%" }} size={8}>
            <Input
              allowClear
              placeholder="Search name, firm, phone, product, city, state, source..."
              prefix={<SearchOutlined />}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setVisibleCount(isMobile ? 10 : 20);
              }}
              size={isMobile ? "middle" : "large"}
            />
            <Row gutter={[8, 8]}>
              <Col xs={12}>
                <Select
                  allowClear
                  placeholder="Inquiry Type"
                  options={inquiryOptions}
                  value={inquiryFilter}
                  onChange={(v) => {
                    setInquiryFilter(v);
                    setVisibleCount(isMobile ? 10 : 20);
                  }}
                  style={{ width: "100%" }}
                  size={isMobile ? "middle" : "large"}
                  suffixIcon={<FilterOutlined />}
                />
              </Col>
              <Col xs={12}>
                <Select
                  allowClear
                  placeholder="State"
                  options={stateOptions}
                  value={stateFilter}
                  onChange={(v) => {
                    setStateFilter(v);
                    setVisibleCount(isMobile ? 10 : 20);
                  }}
                  style={{ width: "100%" }}
                  size={isMobile ? "middle" : "large"}
                  suffixIcon={<FilterOutlined />}
                />
              </Col>
            </Row>
            {(search || inquiryFilter || stateFilter) && (
              <Button
                onClick={resetFilters}
                size={isMobile ? "middle" : "large"}
              >
                Clear filters
              </Button>
            )}
          </Space>
        </Card>
      </div>
      {loading ? (
        <div style={{ textAlign: "center", marginTop: 40 }}>
          <Spin size="large" />
        </div>
      ) : filtered.length === 0 ? (
        <Card
          style={{
            marginTop: 12,
            borderRadius: 12,
            border: "1px solid #f0f0f0",
          }}
        >
          <Empty description="No leads found" />
        </Card>
      ) : (
        <List
          style={{ marginTop: 12 }}
          dataSource={visibleLeads}
          renderItem={(item) => {
            const name = item?.Name || "Unknown";
            const source = item?.Source || "-";
            const inquiry = item?.InquiryType || "-";

            return (
              <Card
                style={{
                  marginBottom: 12,
                  borderRadius: 12,
                  border: "1px solid #e6f7ff",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                  cursor: "default",
                }}
                bodyStyle={{ padding: isMobile ? 12 : 16 }}
              >
                <Row gutter={[8, 8]} align="middle">
                  <Col xs={24}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        gap: 8,
                      }}
                    >
                      <Space direction="vertical" size={2} style={{ minWidth: 0 }}>
                        <Text strong style={{ fontSize: isMobile ? 14 : 16 }}>
                          {name}
                        </Text>
                        {item?.FirmName && (
                          <Text style={{ fontSize: 13 }}>
                            {item.FirmName}
                            {item.GSTIN && (
                              <Tag
                                color="green"
                                icon={<SafetyCertificateOutlined />}
                                style={{ marginLeft: 6 }}
                              >
                                GST registered
                              </Tag>
                            )}
                          </Text>
                        )}
                        <Text type="secondary" style={{ fontSize: isMobile ? 12 : 13 }}>
                          {fmtDate(item?.InquireDate)} • {source}
                        </Text>
                      </Space>
                      <Space direction="vertical" size={4} align="end">
                        <Tag color={tagColorByInquiry(inquiry)} style={{ fontSize: 12, marginRight: 0 }}>
                          {inquiry}
                        </Tag>
                        {item?.EnquiryCount > 1 && (
                          <Tooltip title="Enquiries from this number">
                            <Tag color="orange" style={{ marginRight: 0 }}>
                              {item.EnquiryCount} enquiries
                            </Tag>
                          </Tooltip>
                        )}
                        {item?.LeadStatus && (
                          <Tooltip title="Status from the desktop CRM">
                            <Tag color={statusTagColor(item.LeadStatus)} style={{ marginRight: 0 }}>
                              {item.LeadStatus}
                            </Tag>
                          </Tooltip>
                        )}
                      </Space>
                    </div>
                  </Col>

                  {item?.HasAccount && <Col xs={24}>{accountWarning(item)}</Col>}

                  {(item?.ProductName || item?.EnquiryMessage) && (
                    <Col xs={24}>
                      <div
                        style={{
                          background: "#fafafa",
                          border: "1px solid #f0f0f0",
                          borderRadius: 8,
                          padding: "8px 10px",
                        }}
                      >
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          Asked for
                        </Text>
                        {item?.ProductName && (
                          <div style={{ fontSize: 13, fontWeight: 600 }}>
                            {item.ProductName}
                            {item.ProductCategory && (
                              <Text type="secondary" style={{ fontWeight: 400 }}>
                                {" "}
                                · {item.ProductCategory}
                              </Text>
                            )}
                          </div>
                        )}
                        {item?.EnquiryMessage && (
                          <Paragraph
                            ellipsis={{ rows: 2 }}
                            style={{ fontSize: 13, margin: 0, color: "#595959" }}
                          >
                            "{item.EnquiryMessage}"
                          </Paragraph>
                        )}
                      </div>
                    </Col>
                  )}

                  <Col xs={24}>
                    <Row gutter={[8, 8]}>
                      <Col xs={12} md={6}>
                        {field("City", item?.City)}
                      </Col>
                      <Col xs={12} md={6}>
                        {field("State", item?.State)}
                      </Col>
                      <Col xs={12} md={6}>
                        {field("Contact", item?.ContactNo)}
                      </Col>
                      <Col xs={12} md={6}>
                        {field("Access requested", fmtDate(item?.AccessRequestedOn))}
                      </Col>
                      <Col xs={24}>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          <WhatsAppOutlined /> Welcome message:{" "}
                        </Text>
                        <Text style={{ fontSize: 12 }}>
                          {item?.WelcomeMessageSent ? "Sent" : "Not sent"}
                        </Text>
                        {item?.LastWhatsAppStatus && (
                          <Tag
                            color={whatsAppStatusColor(item.LastWhatsAppStatus)}
                            style={{ marginLeft: 8 }}
                          >
                            Last message: {item.LastWhatsAppStatus}
                          </Tag>
                        )}
                      </Col>
                    </Row>
                  </Col>

                  <Col xs={24}>
                    <Space
                      wrap
                      style={{ width: "100%", justifyContent: "flex-end" }}
                    >
                      <Button
                        icon={<InfoCircleOutlined />}
                        size={isMobile ? "middle" : "large"}
                        onClick={() => openDetails(item)}
                      >
                        More details
                      </Button>
                      <Button
                        icon={<PhoneOutlined />}
                        size={isMobile ? "middle" : "large"}
                        onClick={(e) => {
                          e.stopPropagation();
                          openDialer(item?.ContactNo);
                        }}
                      >
                        Call
                      </Button>
                      {approveButton(item, isMobile ? "middle" : "large")}
                    </Space>
                  </Col>
                </Row>
              </Card>
            );
          }}
        />
      )}
      {!loading && filtered.length > visibleLeads.length && (
        <div style={{ textAlign: "center", margin: "12px 0 24px" }}>
          <Button onClick={loadMore} size={isMobile ? "middle" : "large"}>
            Load more
          </Button>
        </div>
      )}

      {/* Lead details */}
      <Drawer
        open={!!detailLead}
        onClose={() => setDetailLead(null)}
        width={isMobile ? "100%" : 560}
        title={
          detailLead ? (
            <div>
              <div>{detailLead.Name || "Unknown"}</div>
              {detailLead.FirmName && (
                <Text type="secondary" style={{ fontSize: 13, fontWeight: 400 }}>
                  {detailLead.FirmName}
                </Text>
              )}
            </div>
          ) : null
        }
        footer={
          detailLead ? (
            <Space style={{ width: "100%", justifyContent: "flex-end" }}>
              <Button icon={<PhoneOutlined />} onClick={() => openDialer(detailLead.ContactNo)}>
                Call
              </Button>
              {approveButton(detailLead, "middle")}
            </Space>
          ) : null
        }
      >
        {detailLead && (
          <Space direction="vertical" size={20} style={{ width: "100%" }}>
            {accountWarning(detailLead)}

            <Descriptions title="Contact & business" column={1} size="small" bordered>
              <Descriptions.Item label="Name">{detailLead.Name || "-"}</Descriptions.Item>
              <Descriptions.Item label="Firm">{detailLead.FirmName || "-"}</Descriptions.Item>
              <Descriptions.Item label="Phone">{detailLead.ContactNo || "-"}</Descriptions.Item>
              <Descriptions.Item label="Email">{detailLead.Email || "-"}</Descriptions.Item>
              <Descriptions.Item label="Address">{detailLead.Address || "-"}</Descriptions.Item>
              <Descriptions.Item label="City / State">
                {detailLead.City || "-"}, {detailLead.State || "-"}
                {detailLead.PinCode ? ` - ${detailLead.PinCode}` : ""}
              </Descriptions.Item>
              {detailLead.GSTIN && (
                <Descriptions.Item label="GSTIN">{detailLead.GSTIN}</Descriptions.Item>
              )}
            </Descriptions>

            <Descriptions title="Latest enquiry" column={1} size="small" bordered>
              <Descriptions.Item label="Source">{detailLead.Source || "-"}</Descriptions.Item>
              <Descriptions.Item label="Type">{detailLead.InquiryType || "-"}</Descriptions.Item>
              <Descriptions.Item label="Date">{fmtDateTime(detailLead.InquireDate)}</Descriptions.Item>
              <Descriptions.Item label="Product">{detailLead.ProductName || "-"}</Descriptions.Item>
              {detailLead.ProductCategory && (
                <Descriptions.Item label="Category">{detailLead.ProductCategory}</Descriptions.Item>
              )}
              <Descriptions.Item label="Message">
                <span style={{ whiteSpace: "pre-wrap" }}>{detailLead.EnquiryMessage || "-"}</span>
              </Descriptions.Item>
              {detailLead.CallDurationSeconds !== null &&
                detailLead.CallDurationSeconds !== undefined && (
                  <Descriptions.Item label="Call duration">
                    {fmtDuration(detailLead.CallDurationSeconds)}
                  </Descriptions.Item>
                )}
              {detailLead.CalledNumber && (
                <Descriptions.Item label="Number they called">{detailLead.CalledNumber}</Descriptions.Item>
              )}
            </Descriptions>

            <Descriptions title="Website access request" column={1} size="small" bordered>
              <Descriptions.Item label="Requested on">
                {fmtDateTime(detailLead.AccessRequestedOn)}
              </Descriptions.Item>
              <Descriptions.Item label="Location entered">
                {[detailLead.RequestCity, detailLead.RequestState].filter(Boolean).join(", ") || "-"}
                {detailLead.RequestPinCode ? ` - ${detailLead.RequestPinCode}` : ""}
              </Descriptions.Item>
            </Descriptions>

            <Descriptions
              title="Business type on approval"
              extra={
                <Button
                  size="small"
                  icon={<ReloadOutlined />}
                  onClick={() => loadLabels(detailLead.LeeadID, true)}
                >
                  Recheck
                </Button>
              }
              column={1}
              size="small"
              bordered
            >
              <Descriptions.Item label="Account type">{labelsText(detailLead.LeeadID)}</Descriptions.Item>
              <Descriptions.Item label="WhatsApp labels">
                {labelsByLead[detailLead.LeeadID]?.data?.Labels?.length
                  ? labelsByLead[detailLead.LeeadID].data.Labels.join(", ")
                  : "-"}
              </Descriptions.Item>
            </Descriptions>

            <Descriptions title="WhatsApp" column={1} size="small" bordered>
              <Descriptions.Item label="Welcome message">
                {detailLead.WelcomeMessageSent ? "Sent" : "Not sent"}
              </Descriptions.Item>
              <Descriptions.Item label="Last message status">
                {detailLead.LastWhatsAppStatus ? (
                  <>
                    <Tag color={whatsAppStatusColor(detailLead.LastWhatsAppStatus)}>
                      {detailLead.LastWhatsAppStatus}
                    </Tag>
                    <Text type="secondary">{fmtDateTime(detailLead.LastWhatsAppStatusOn)}</Text>
                  </>
                ) : (
                  "-"
                )}
              </Descriptions.Item>
            </Descriptions>

            <div>
              <Title level={5} style={{ marginTop: 0 }}>
                CRM follow-up
              </Title>
              <div style={{ marginBottom: 12 }}>
                <Text type="secondary">Status: </Text>
                {detailLead.LeadStatus ? (
                  <Tag color={statusTagColor(detailLead.LeadStatus)}>{detailLead.LeadStatus}</Tag>
                ) : (
                  "-"
                )}
              </div>
              {detailLead.Activity && detailLead.Activity.length > 0 ? (
                <Timeline
                  items={detailLead.Activity.map((a, i) => ({
                    key: i,
                    children: (
                      <div>
                        <Text strong>{a.Type}</Text>{" "}
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {fmtDateTime(a.On)}
                        </Text>
                        {a.Details && <div style={{ fontSize: 13 }}>{a.Details}</div>}
                      </div>
                    ),
                  }))}
                />
              ) : (
                <Text type="secondary">No updates from the desktop CRM yet.</Text>
              )}
            </div>

            <div>
              <Title level={5} style={{ marginTop: 0 }}>
                All enquiries ({detailLead.EnquiryCount || 1})
              </Title>
              <Timeline
                items={(detailLead.Enquiries || []).map((e) => ({
                  key: e.LeadID,
                  children: (
                    <div>
                      <Text strong>{fmtDateTime(e.Date)}</Text>{" "}
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {[e.Source, e.InquiryType].filter(Boolean).join(" · ")}
                      </Text>
                      {e.ProductName && <div style={{ fontSize: 13 }}>{e.ProductName}</div>}
                      {e.Message && (
                        <div style={{ fontSize: 13, color: "#595959", whiteSpace: "pre-wrap" }}>
                          "{e.Message}"
                        </div>
                      )}
                      {e.CallDurationSeconds !== null && e.CallDurationSeconds !== undefined && (
                        <div style={{ fontSize: 12, color: "#8c8c8c" }}>
                          Call: {fmtDuration(e.CallDurationSeconds)}
                        </div>
                      )}
                    </div>
                  ),
                }))}
              />
            </div>
          </Space>
        )}
      </Drawer>
    </div>
  );
};

export default LeadsToApprove;
